import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function Login() {
  const { login, register, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState(localStorage.getItem("sg_last_email") || "");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(email, password, name);
      nav("/");
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0A0A] px-4">
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="relative w-full max-w-md sg-fade">
        <div className="mb-8 flex items-center gap-2">
          <Activity size={26} className="text-[#00F0FF]" strokeWidth={2.5} />
          <span className="font-head text-2xl font-black tracking-tight">KLONDIKE<span className="text-[#00F0FF]">SWING</span></span>
        </div>
        <div className="border border-white/10 bg-[#111111] p-6">
          <h1 className="font-head text-xl font-bold">{mode === "login" ? "Terminal Access" : "Create Account"}</h1>
          <p className="mt-1 text-sm text-gray-500">Evidence-based swing-trade setup grading across the S&P 500.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && (
              <Field label="Name" testid="name-input" value={name} onChange={setName} type="text" placeholder="Jane Trader" />
            )}
            <Field label="Email" testid="email-input" value={email} onChange={setEmail} type="email" placeholder="you@fund.com" />
            <Field label="Password" testid="password-input" value={password} onChange={setPassword} type="password" placeholder="••••••••" />

            {err && <div data-testid="auth-error" className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 text-xs text-[#FF3B30]">{err}</div>}

            <button
              type="submit" disabled={busy} data-testid="auth-submit"
              className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#00F0FF] py-2.5 font-mono-sg text-sm font-bold text-black transition-colors duration-200 hover:bg-[#00F0FF]/80 disabled:opacity-50"
            >
              {busy && <Loader2 size={15} className="animate-spin" />}
              {mode === "login" ? "AUTHENTICATE" : "REGISTER"}
            </button>
          </form>

          <button
            data-testid="auth-toggle"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setErr(""); }}
            className="mt-4 w-full text-center text-xs text-gray-500 transition-colors duration-200 hover:text-gray-300"
          >
            {mode === "login" ? "No account? Register →" : "Have an account? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type, placeholder, testid }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs uppercase tracking-wide text-gray-500">{label}</span>
      <input
        data-testid={testid}
        type={type} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        required
        className="w-full rounded-sm border border-white/10 bg-transparent px-3 py-2 font-mono-sg text-sm text-white outline-none transition-colors duration-200 placeholder:text-gray-700 focus:border-[#00F0FF]"
      />
    </label>
  );
}
